import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lms/shared-types';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { customFieldGroupSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { usePermissions } from '@/hooks/usePermissions';

interface GroupForm {
  name: string;
}

interface FieldGroup {
  id: string;
  name: string;
  _count?: { fields?: number };
}

const SECTION = 'lms-credentials-glass-panel rounded-xl border border-dash-outline-variant p-6 shadow-sm md:p-8';
const LABEL = 'text-sm font-medium text-dash-on-surface-variant';
const FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white px-4 py-3 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

export function UserCustomFieldGroupFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const isEdit = Boolean(id);
  const canDelete = isEdit && hasPermission(PERMISSIONS.CAMPOS_USUARIO_ELIMINAR);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: existing, isLoading } = useQuery({
    queryKey: ['user-custom-field-groups', id],
    queryFn: () => api<FieldGroup>(`/user-custom-fields/groups/${id}`),
    enabled: isEdit,
  });

  const assigned = existing?._count?.fields ?? 0;

  const { register, handleSubmit } = useForm<GroupForm>({
    values: existing ? { name: existing.name } : undefined,
    defaultValues: { name: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: GroupForm) => {
      const body = { name: values.name.trim() };
      return isEdit
        ? api(`/user-custom-fields/groups/${id}`, { method: 'PATCH', body })
        : api('/user-custom-fields/groups', { method: 'POST', body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['user-custom-field-groups'] });
      if (isEdit && id) void queryClient.invalidateQueries({ queryKey: ['user-custom-field-groups', id] });
      toast(customFieldGroupSuccessToast(isEdit ? 'edit' : 'create'));
      navigate('/campos-personalizados/grupos');
    },
    onError: (error) => {
      toast(
        roleActionErrorToast(
          error,
          isEdit ? 'No se pudo guardar el grupo de campos' : 'No se pudo crear el grupo de campos',
        ),
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api(`/user-custom-fields/groups/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['user-custom-field-groups'] });
      void queryClient.invalidateQueries({ queryKey: ['user-custom-fields'] });
      toast(customFieldGroupSuccessToast('delete'));
      navigate('/campos-personalizados/grupos');
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el grupo de campos'));
    },
  });

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <div className="mt-1 shrink-0">
          <HubBackLink to="/campos-personalizados/grupos" />
        </div>
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
            {isEdit ? 'Editar grupo' : 'Nuevo grupo'}
          </h1>
          <p className="mt-1 text-dash-on-surface-variant">
            {isEdit
              ? 'Actualice el nombre de este grupo de campos personalizados.'
              : 'Cree un grupo para organizar los campos del perfil de usuario.'}
          </p>
        </div>
      </div>

      {isEdit && isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <form id="user-custom-field-group-form" className="space-y-6" onSubmit={handleSubmit((values) => mutation.mutate(values))}>
          <section className={SECTION}>
            <div className="max-w-xl space-y-2">
              <label htmlFor="group-name" className={LABEL}>
                Nombre <span className="text-error-600">*</span>
              </label>
              <input
                id="group-name"
                type="text"
                required
                className={FIELD}
                {...register('name', { required: true })}
              />
            </div>
          </section>

          <div className="flex flex-col-reverse items-stretch justify-end gap-4 border-t border-dash-outline-variant pt-8 sm:flex-row sm:items-center">
            {canDelete ? (
              <button
                type="button"
                className="mr-auto inline-flex items-center justify-center rounded-full border border-error-200 px-8 py-3 text-sm font-bold text-error-600 transition-all hover:bg-error-50 active:scale-[0.98] sm:w-auto disabled:cursor-not-allowed disabled:opacity-40"
                disabled={assigned > 0}
                title={assigned > 0 ? 'Reasigne los campos antes de eliminar el grupo' : 'Eliminar'}
                onClick={() => setDeleteOpen(true)}
              >
                Eliminar
              </button>
            ) : null}
            <Link
              to="/campos-personalizados/grupos"
              className="inline-flex items-center justify-center rounded-full border border-dash-primary px-8 py-3 text-sm font-bold text-dash-primary transition-all hover:bg-brand-50 active:scale-[0.98] sm:w-auto"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              form="user-custom-field-group-form"
              disabled={mutation.isPending}
              className="inline-flex items-center justify-center rounded-full bg-dash-primary px-10 py-3 text-sm font-bold text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98] sm:w-auto disabled:opacity-60"
            >
              {mutation.isPending ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear grupo'}
            </button>
          </div>
        </form>
      )}

      <ConfirmModal
        open={deleteOpen}
        title="Eliminar grupo"
        description={`¿Está seguro de eliminar el grupo «${existing?.name ?? ''}»?`}
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setDeleteOpen(false)}
      />
    </div>
  );
}
